import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Building2, Home, Shirt, CheckCircle2, Loader2, AlertCircle, Droplets } from 'lucide-react';
import { fetchStockByIds, fetchStockByNames } from '@/services/movimientosLenceria.service.js';
import { fetchInsumoStockByNames } from '@/pages/lavanderia/insumos/services/inventario-insumos.services.js';

const AlistarLenceriaModal = ({ isOpen, onClose, solicitud, onSuccess }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStock, setIsLoadingStock] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  // Cargar stock disponible cuando se abre el modal
  useEffect(() => {
    if (isOpen && solicitud) {
      loadStockDisponible();
    }
  }, [isOpen, solicitud]);

  const loadStockDisponible = async () => {
    setIsLoadingStock(true);
    try {
      // Separar items en prendas e insumos
      const itemsPrendas = solicitud.items.filter(item => item.tipo_item !== 'insumo');
      const itemsInsumos = solicitud.items.filter(item => item.tipo_item === 'insumo');

      // Cargar stock de prendas e insumos en paralelo
      const [stockPrendasResponse, stockInsumosResponse] = await Promise.all([
        itemsPrendas.length > 0 
          ? fetchStockByNames(itemsPrendas.map(item => item.nombre))
          : { data: {} },
        itemsInsumos.length > 0 
          ? fetchInsumoStockByNames(itemsInsumos.map(item => item.nombre))
          : { data: {} },
      ]);

      const stockPrendasMap = stockPrendasResponse?.data || {};
      const stockInsumosMap = stockInsumosResponse?.data || {};

      // Mapear los items con su stock correspondiente
      const itemsConStock = solicitud.items.map(item => {
        const isInsumo = item.tipo_item === 'insumo';
        const stock = isInsumo 
          ? stockInsumosMap[item.nombre] 
          : stockPrendasMap[item.nombre];
        const stockDisponible = stock?.cantidad_disponible ?? 0;

        return {
          ...item,
          stock_disponible: stockDisponible,
          cantidad_alistar: item.cantidad,
          tiene_stock: stockDisponible >= item.cantidad,
          inventario_id: stock?.id
        };
      });

      setItems(itemsConStock);
      setObservaciones('');
    } catch (error) {
      console.error('Error al cargar stock:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el stock disponible',
        variant: 'destructive'
      });
      // En caso de error, inicializar sin stock
      const itemsSinStock = solicitud.items.map(item => ({
        ...item,
        stock_disponible: 0,
        cantidad_alistar: 0,
        tiene_stock: false
      }));
      setItems(itemsSinStock);
    } finally {
      setIsLoadingStock(false);
    }
  };

  const handleCantidadChange = (index, value) => {
    const newItems = [...items];
    const cantidad = parseInt(value) || 0;
    newItems[index].cantidad_alistar = Math.max(0, Math.min(cantidad, newItems[index].cantidad));
    setItems(newItems);
  };

  const handleAlistar = async () => {
    // Validar que haya items para alistar
    const itemsAlistar = items.filter(item => item.cantidad_alistar > 0);
    
    if (itemsAlistar.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe alistar al menos un item con cantidad mayor a 0',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      // Obtener usuario actual
      const usuario = {
        id: localStorage.getItem('userId') || '68ee96b27238402c10e314ce',
        nombre: localStorage.getItem('userName') || 'Usuario'
      };

      // Calcular peso total de los items a alistar
      const pesoTotalAlistar = itemsAlistar.reduce((sum, item) => 
        sum + ((item.peso || 0) * item.cantidad_alistar), 0
      );

      const data = {
        solicitud_id: solicitud._id,
        alistador: usuario,
        items: itemsAlistar.map(item => ({
          id: item.id,
          nombre: item.nombre,
          cantidad: item.cantidad_alistar,
          peso: item.peso || 0,
          tipo: item.tipo,
          tipo_item: item.tipo_item || undefined,
          observacion: item.observacion || ''
        })),
        peso_total: pesoTotalAlistar,
        observaciones: observaciones
      };

      // Llamar al endpoint de alistamiento de entregas-lenceria
      const response = await fetch(`${API_URL}/entregas-lenceria/alistar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al alistar lencería');
      }

      const result = await response.json();
      
      // Verificar si la respuesta es exitosa
      if (!result.state) {
        throw new Error(result.message || 'Error al alistar lencería');
      }

      toast({
        title: 'Éxito',
        description: result.message || 'Lencería alistada correctamente',
      });

      // Llamar a onSuccess primero para recargar datos
      if (onSuccess) {
        await onSuccess();
      }
      
      // Pequeño delay para asegurar que el toast se muestre
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      console.error('Error al alistar lencería:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo alistar la lencería',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!solicitud) return null;

  const totalAlistar = items.reduce((sum, item) => sum + (item.cantidad_alistar || 0), 0);
  const totalSolicitado = items.reduce((sum, item) => sum + item.cantidad, 0);
  const pesoTotal = items.reduce((sum, item) => sum + ((item.peso || 0) * (item.cantidad_alistar || 0)), 0);
  const todosConStock = items.every(item => item.tiene_stock);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Shirt className="h-6 w-6 text-blue-600" />
            Alistar Lencería
          </DialogTitle>
          <DialogDescription>
            Confirmar cantidades de prendas a alistar para entrega
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información de la Solicitud */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <div>
                <div className="text-xs text-gray-500">Edificio</div>
                <div className="font-medium">{solicitud.edificio?.nombre}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Home className="h-4 w-4 text-green-600" />
              <div>
                <div className="text-xs text-gray-500">Hospedaje</div>
                <div className="font-medium">{solicitud.hospedaje?.nombre || solicitud.nombrePropiedad}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Items */}
          {isLoadingStock ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2">Cargando stock disponible...</span>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Solicitado</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">A Alistar</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item, index) => {
                    const isInsumo = item.tipo_item === 'insumo';
                    return (
                    <tr key={index} className={`${isInsumo ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-gray-50'} ${!item.tiene_stock ? (isInsumo ? 'bg-amber-100/60' : 'bg-red-50') : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isInsumo 
                          ? <Droplets className="h-4 w-4 text-amber-600" />
                          : <Shirt className="h-4 w-4 text-blue-600" />
                        }
                        <div>
                          <div className="font-medium text-gray-900">{item.nombre}</div>
                          {item.observacion && (
                            <div className="text-xs text-gray-500 mt-1">{item.observacion}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isInsumo ? (
                        <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 border border-amber-300">
                          Insumo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          {item.tipo}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-gray-100 text-gray-800 font-semibold">
                        {item.cantidad}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={item.tiene_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {item.stock_disponible}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        min="0"
                        max={item.cantidad}
                        value={item.cantidad_alistar}
                        onChange={(e) => handleCantidadChange(index, e.target.value)}
                        className="w-20 text-center mx-auto"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.tiene_stock ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                      ) : (
                        <AlertCircle className={`h-5 w-5 mx-auto ${isInsumo ? 'text-amber-600' : 'text-red-600'}`} />
                      )}
                    </td>
                  </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Resumen */}
          <div className="p-4 bg-blue-50 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">Total a Alistar</div>
                <div className="text-2xl font-bold text-blue-600">
                  {totalAlistar} items
                  {(() => {
                    const prendasCount = items.filter(i => i.tipo_item !== 'insumo' && i.cantidad_alistar > 0).reduce((s, i) => s + i.cantidad_alistar, 0);
                    const insumosCount = items.filter(i => i.tipo_item === 'insumo' && i.cantidad_alistar > 0).reduce((s, i) => s + i.cantidad_alistar, 0);
                    const parts = [];
                    if (prendasCount > 0) parts.push(`${prendasCount} prendas`);
                    if (insumosCount > 0) parts.push(`${insumosCount} insumos`);
                    return parts.length > 0 ? <span className="text-sm font-normal text-gray-500 ml-2">({parts.join(' + ')})</span> : null;
                  })()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 text-right">Peso Total (prendas)</div>
                <div className="text-2xl font-bold text-purple-600">{pesoTotal.toFixed(0)} g</div>
              </div>
            </div>
            {!todosConStock && (
              <div className="flex items-center gap-2 text-amber-600 pt-2 border-t border-amber-200">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Algunos items no tienen stock completo</span>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Observaciones (opcional)
            </label>
            <Textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Agregar observaciones sobre el alistamiento de lencería..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAlistar} 
            disabled={isLoading || isLoadingStock || totalAlistar === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Alistando...
              </>
            ) : (
              <>
                <Shirt className="h-4 w-4 mr-2" />
                Guardar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlistarLenceriaModal;
